/**
 * toolStatusLabels — maps an in-flight backend tool name to a GENERAL,
 * customer-friendly i18n key, so the "Ion is working…" cue can say what Ion is
 * actually doing ("Ranking cells", "Selecting candidate cells", …) without
 * leaking the raw tool name or its JSON input.
 *
 * Deterministic + frontend-only — no backend/wire change. Synonyms are grouped
 * onto a single phrase so the line stays short and stable across re-ranks.
 * Tool names mirror backend/app/api/v1/chat/tools/builtin/. Anything not mapped
 * (ask_user, navigate, get_current_time, future tools) falls back to the
 * generic `ion.working` phrase via toolActivityKey() returning null.
 */

export const TOOL_ACTIVITY_KEYS = {
  // Cell-selection ranking
  rank_cells_for_application: 'ion.tool_activity.ranking',

  // Choosing / listing the candidate set
  search_cells: 'ion.tool_activity.selecting',
  list_selection_branches: 'ion.tool_activity.selecting',
  list_available_curves: 'ion.tool_activity.selecting',
  list_metrics: 'ion.tool_activity.selecting',
  list_plottable_metrics: 'ion.tool_activity.selecting',

  // Numeric work (deriving, fetching series, aggregating, comparing)
  derive_metric: 'ion.tool_activity.calculating',
  fetch_series: 'ion.tool_activity.calculating',
  calculate: 'ion.tool_activity.calculating',
  series_op: 'ion.tool_activity.calculating',
  aggregate_metric: 'ion.tool_activity.calculating',
  aggregate_metric_grouped: 'ion.tool_activity.calculating',
  get_cell_metric: 'ion.tool_activity.calculating',
  get_cell_metrics: 'ion.tool_activity.calculating',
  compare_cells_table: 'ion.tool_activity.calculating',

  // Application profile load
  apply_application_profile: 'ion.tool_activity.profile',

  // Plot building
  plot_curves: 'ion.tool_activity.plotting',
  plot_series: 'ion.tool_activity.plotting',
  plot_scatter: 'ion.tool_activity.plotting',
  plot_bar: 'ion.tool_activity.plotting',
  plot_aggregate: 'ion.tool_activity.plotting',

  // Knowledge / web
  get_knowledge: 'ion.tool_activity.knowledge',
  web_search: 'ion.tool_activity.web',
}

/**
 * Pick the i18n key for the current activity from a message's toolCalls.
 * Returns the key for the FIRST running tool (stable, call-order), or null
 * when nothing mapped is running (→ caller uses the generic working cue).
 *
 * @param {Array<{ name?: string, status?: string }>} toolCalls
 * @returns {string | null}
 */
export function toolActivityKey(toolCalls) {
  if (!Array.isArray(toolCalls)) return null
  for (const call of toolCalls) {
    if (call && call.status === 'running') {
      return TOOL_ACTIVITY_KEYS[call.name] || null
    }
  }
  return null
}

/**
 * The FIRST tool call currently `running`, or null. The working cue uses this
 * both to pick the activity label and to read `startedAt` for the elapsed
 * counter, so the two never disagree about which tool is in flight.
 *
 * @param {Array<{ name?: string, status?: string, startedAt?: number, input?: object }>} toolCalls
 */
export function runningTool(toolCalls) {
  if (!Array.isArray(toolCalls)) return null
  for (const call of toolCalls) {
    if (call && call.status === 'running') return call
  }
  return null
}

/**
 * Resolve the running tool into an { key, params } descriptor for `t()`.
 * Returns null when nothing mapped is running (caller falls back to the
 * generic `ion.working` phrase).
 *
 * P3 — a step-aware label reads as more purposeful than a generic phrase, so
 * when the ranking tool exposes its candidate count we surface it
 * ("Ranking 30 candidate cells"). Everything else uses its plain mapped phrase.
 *
 * @param {Array} toolCalls
 * @returns {{ key: string, params: (object|null) } | null}
 */
export function toolActivityDescriptor(toolCalls) {
  const call = runningTool(toolCalls)
  if (!call) return null
  const key = TOOL_ACTIVITY_KEYS[call.name] || null
  if (!key) return null
  if (call.name === 'rank_cells_for_application') {
    const cands = call.input && call.input.candidate_cells
    const count = Array.isArray(cands) ? cands.length : 0
    if (count > 0) return { key: 'ion.tool_activity.ranking_count', params: { count } }
  }
  return { key, params: null }
}
